import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FaChartBar,
  FaWallet,
  FaMoneyBillWave,
  FaUsers,
  FaTrophy,
  FaExchangeAlt,
  FaUser,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa";

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Dashboard", path: "/dashboard", icon: FaChartBar },
    { name: "Withdraw", path: "/withdraw", icon: FaMoneyBillWave },
    { name: "Referrals", path: "/referrals", icon: FaUsers },
    { name: "Stages", path: "/stages", icon: FaTrophy },
    { name: "Transactions", path: "/transactions", icon: FaExchangeAlt },
    { name: "Profile", path: "/profile", icon: FaUser },
  ];

  // Only show deposit for non-activated users
  if (!user?.isActivated) {
    navigation.splice(1, 0, {
      name: "Deposit",
      path: "/deposit",
      icon: FaWallet,
    });
  }

  if (user?.isAdmin) {
    navigation.push({ name: "Admin", path: "/admin", icon: FaCog });
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900/70 border-r border-white/10 backdrop-blur-xl shadow-[0_0_30px_rgba(15,23,42,0.5)] transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-white/10">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-violet-600 rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(34,211,238,0.6)]">
                <span className="text-white font-bold text-xl">L</span>
              </div>
              <h1 className="ml-3 text-xl font-bold text-white">LOOPS</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-300 hover:text-white"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-white/10 bg-white/5">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-violet-600 rounded-full flex items-center justify-center text-white font-semibold shadow-[0_0_12px_rgba(56,189,248,0.6)]">
                {user?.fullName?.charAt(0).toUpperCase()}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {user?.fullName}
                </p>
                <p className="text-xs text-slate-300 flex items-center">
                  <FaWallet className="mr-1 text-cyan-300" />$
                  {user?.walletBalance?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>
            {!user?.isActivated && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-200 bg-amber-500/20 rounded border border-amber-400/30">
                  Not Activated
                </span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto">
            <ul className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={isActive ? "nav-link-active" : "nav-link"}
                    >
                      <Icon
                        className={`mr-3 text-lg ${
                          isActive ? "text-cyan-300" : "text-slate-400"
                        }`}
                      />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors"
            >
              <FaSignOutAlt className="mr-3 text-lg" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-14 sm:h-16 bg-slate-900/90 border-b border-white/10 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden text-slate-300 hover:text-white p-2 -ml-2 rounded-lg active:bg-white/10 touch-manipulation"
          >
            <FaBars size={22} />
          </button>
          <div className="flex-1 lg:flex lg:items-center lg:justify-between">
            <h2 className="text-base sm:text-lg font-semibold text-white ml-2 sm:ml-4 lg:ml-0 truncate">
              {navigation.find((item) => item.path === location.pathname)
                ?.name || "Dashboard"}
            </h2>
            <div className="hidden lg:flex items-center space-x-4">
              <div className="text-sm text-slate-300">
                Welcome back,{" "}
                <span className="font-medium text-white">{user?.fullName}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-slate-950 pb-20 lg:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <Outlet />
          </div>
        </main>

        {/* Mobile Bottom Tab Bar */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <div className="flex items-center justify-around px-1 pt-2 pb-1">
            {(() => {
              const bottomTabs = [
                { name: "Home", path: "/dashboard", icon: FaChartBar },
                { name: "Withdraw", path: "/withdraw", icon: FaMoneyBillWave },
                { name: "Referrals", path: "/referrals", icon: FaUsers },
                { name: "Stages", path: "/stages", icon: FaTrophy },
                { name: "Profile", path: "/profile", icon: FaUser },
              ];
              return bottomTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = location.pathname === tab.path;
                return (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`flex flex-col items-center justify-center py-1 px-2 min-w-[56px] rounded-lg transition-all touch-manipulation ${
                      isActive ? "text-cyan-300" : (
                        "text-slate-400 active:text-slate-200"
                      )
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center mb-0.5 transition-all ${
                        isActive ?
                          "bg-cyan-500/20 shadow-[0_0_12px_rgba(34,211,238,0.3)]"
                        : ""
                      }`}
                    >
                      <Icon
                        className={`text-base ${isActive ? "text-cyan-300" : ""}`}
                      />
                    </div>
                    <span
                      className={`text-[10px] font-medium ${
                        isActive ? "text-cyan-300" : ""
                      }`}
                    >
                      {tab.name}
                    </span>
                    {isActive && (
                      <div className="w-4 h-0.5 rounded-full bg-cyan-400 mt-0.5" />
                    )}
                  </Link>
                );
              });
            })()}
          </div>
        </nav>

        {/* Footer - desktop only */}
        <footer className="hidden lg:block bg-slate-900/80 border-t border-white/10 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs text-slate-400">
              © 2026 LOOPS - Digital Chit Fund Donation System. All rights
              reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
